// The reference rule on the scoring panel. Migrations 2800 and 2801.
//
// Every word about what is required comes from interview_decision_eval_v2 in
// Postgres: which reference counts as Reference A and why, whether Reference B
// is required, not required or not yet known, and every trigger that fired.
// Nothing here decides anything. It lays the evaluation out, and offers the
// forms whose rules the database enforces.
//
// Sealing is the database's job too. A viewer still scoring Interview 2 gets
// an evaluation with every score-based trigger already removed, so this file
// never holds a truth average it would have to hide.
import { useState } from 'react'
import { AlertTriangle, CheckCircle2, Circle, HelpCircle, Info, ShieldAlert } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Checkbox } from '../../lib/shadcn/checkbox'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { Chip, type ChipTone } from './Chip'

export type RelationshipType = {
  type_key: string
  label: string
  held_authority: boolean
  needs_organisation: boolean
}

export type RefereeFlags = {
  coordination: {
    other_application_id: string
    other_applicant_name: string | null
    other_referee_name: string
    other_added_at: string
  }[]
  inside_business: { person_name: string | null; as: 'driver' | 'servant' }[]
}

export type RuleTrigger = { key: string; label: string; detail: string; score_based: boolean }

type Slot = {
  status: string
  why: string
  met?: boolean
  referee_name?: string | null
  score?: number | null
}

export type ReferenceRule =
  | {
      rule_kind: 'flat'
      policy_version: string
      required: number
      path: string | null
      triggers: RuleTrigger[]
      summary: string
    }
  | {
      rule_kind: 'conditional'
      policy_version: string
      interview_2_date: string | null
      reference_a: Slot & { status: 'met' | 'open' }
      reference_b: Slot & { status: 'required' | 'not_required' | 'not_yet_known'; met: boolean }
      same_person_as_a: string[]
      triggers: RuleTrigger[]
      uber: { qualifies: boolean; summary: string }
      coordination: (RefereeFlags['coordination'][number] & { referee_id: string; referee_name: string })[]
      inside_business: (RefereeFlags['inside_business'][number] & { referee_id: string; referee_name: string })[]
      discretion: { reason: string; by_name: string | null; at: string }[]
      unreachable: { count: number; hold_after: number | null; held: boolean; caller: string }
      required: number | null
      path: string | null
      policy: { reference_pass_score: number; reference_knockout_items: string[] }
    }

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Africa/Johannesburg' })
}

// The words for a recorded path, used on the decision line and the report.
export function pathLabel(path: string | null | undefined): string {
  switch (path) {
    case 'one_reference_uber_substitute':
      return 'One reference, the Uber record stands in for the second'
    case 'one_reference_not_required_other':
      return 'One reference'
    case 'two_references':
      return 'Two references'
    case 'flat_rule':
      return 'Two references, the rule before 3 Oct 2026'
    case 'not_reached':
      return 'Decided before the references'
    default:
      return 'Not settled yet'
  }
}

// "R0, R6b, R7 or R8", from the policy's list.
export function orList(xs: string[]): string {
  if (xs.length <= 1) return xs.join('')
  return `${xs.slice(0, -1).join(', ')} or ${xs[xs.length - 1]}`
}

// One line naming the types that can be the first reference, from the data.
export function authorityLine(types: RelationshipType[]): string {
  const names = types.filter((t) => t.held_authority).map((t) => t.label.toLowerCase())
  if (names.length === 0) return ''
  return `Can be the first reference: ${names.join(', ')}.`
}

const B_TONE: Record<string, ChipTone> = { required: 'amber', not_required: 'green', not_yet_known: 'neutral' }
const B_LABEL: Record<string, string> = {
  required: 'Required',
  not_required: 'Not required',
  not_yet_known: 'Not yet known',
}

function SlotCard({ title, chip, tone, why, icon }: { title: string; chip: string; tone: ChipTone; why: string; icon: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-background p-3" data-testid={`slot-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {icon}
          {title}
        </span>
        <Chip tone={tone} className="whitespace-normal text-left">
          {chip}
        </Chip>
      </div>
      <p className="mt-1.5 text-xs text-foreground">{why}</p>
    </div>
  )
}

export function ReferenceRuleSummary({ rule }: { rule: ReferenceRule | null | undefined }) {
  if (!rule) return null
  if (rule.rule_kind === 'flat') {
    return (
      <div className="rounded-md border border-border bg-muted/50 px-3 py-2 text-xs text-muted-foreground" data-testid="reference-rule-flat">
        <span className="font-medium text-foreground">Rule {rule.policy_version}. </span>
        {rule.summary}
      </div>
    )
  }

  const a = rule.reference_a
  const b = rule.reference_b
  const bChip = b.status === 'required' ? (b.met ? 'Required, done' : 'Required') : B_LABEL[b.status] ?? b.status
  const bTone: ChipTone = b.status === 'required' && b.met ? 'green' : B_TONE[b.status] ?? 'neutral'

  return (
    <div className="space-y-2" data-testid="reference-rule">
      <div className="grid gap-2 sm:grid-cols-2">
        <SlotCard
          title="Reference A"
          chip={a.status === 'met' ? 'Done' : 'Open'}
          tone={a.status === 'met' ? 'green' : 'amber'}
          why={a.why}
          icon={a.status === 'met' ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
        />
        <SlotCard
          title="Reference B"
          chip={bChip}
          tone={bTone}
          why={
            b.met && b.referee_name
              ? `${b.referee_name} scored ${b.score} of 30.`
              : b.status === 'required' && rule.triggers.length > 0
                ? `Required for ${rule.triggers.length === 1 ? 'one reason' : `${rule.triggers.length} reasons`}, listed below. Still open.`
                : b.why
          }
          icon={b.status === 'not_yet_known' ? <HelpCircle className="h-3.5 w-3.5" /> : b.met || b.status === 'not_required' ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
        />
      </div>

      {rule.triggers.length > 0 && (
        <div className="rounded-md border border-border bg-background px-3 py-2 text-xs" data-testid="reference-triggers">
          <p className="font-medium text-foreground">Why a second reference is required</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-muted-foreground">
            {rule.triggers.map((t) => (
              <li key={t.key}>
                <span className="text-foreground">{t.label}.</span> {t.detail}
              </li>
            ))}
          </ul>
        </div>
      )}

      {rule.coordination.length > 0 && (
        <div
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
          data-testid="coordination-warning"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div className="space-y-0.5">
            <p className="font-medium">A referee's number was also given by another applicant</p>
            {rule.coordination.map((c) => (
              <p key={`${c.referee_id}-${c.other_application_id}`}>
                {c.referee_name}'s number was given as a referee ({c.other_referee_name}) by{' '}
                <span className="font-medium">{c.other_applicant_name ?? 'another applicant'}</span> on {shortDate(c.other_added_at)}.
                It may be a coincidence. Ask both how they know this person.
              </p>
            ))}
          </div>
        </div>
      )}

      {rule.inside_business.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-xs text-muted-foreground" data-testid="inside-business-note">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div className="space-y-0.5">
            {rule.inside_business.map((p) => (
              <p key={`${p.referee_id}-${p.person_name}`}>
                {p.referee_name}'s number belongs to {p.person_name ?? 'someone'}, a current {p.as === 'driver' ? 'Fleetly driver' : 'servant'}. The
                referee is inside the business.
              </p>
            ))}
          </div>
        </div>
      )}

      {rule.same_person_as_a.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {rule.same_person_as_a.join(', ')} is the same person as Reference A, so it counts once.
        </p>
      )}

      <p className="text-xs text-muted-foreground">
        <span className="font-medium text-foreground">Uber record: </span>
        {rule.uber.qualifies ? `strong, ${rule.uber.summary.replace(/^Strong Uber record: /, '')}.` : rule.uber.summary}
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Adding a referee, or classifying one recorded before the type existed
// ---------------------------------------------------------------------------

export type RefereeFormValues = {
  referee_name: string
  phone: string
  relationship_type: string
  capacity_note: string
  organisation: string
  known_from: string
  known_to: string
  consent: boolean
}

export const EMPTY_REFEREE: RefereeFormValues = {
  referee_name: '',
  phone: '',
  relationship_type: '',
  capacity_note: '',
  organisation: '',
  known_from: '',
  known_to: '',
  consent: false,
}

export function RefereeForm({
  types,
  mode,
  initial,
  busy,
  onSubmit,
  onCancel,
}: {
  types: RelationshipType[]
  mode: 'add' | 'classify'
  initial?: Partial<RefereeFormValues>
  busy: boolean
  onSubmit: (v: RefereeFormValues) => void
  onCancel: () => void
}) {
  const [form, setForm] = useState<RefereeFormValues>({ ...EMPTY_REFEREE, ...initial })
  const type = types.find((t) => t.type_key === form.relationship_type)
  const set = (patch: Partial<RefereeFormValues>) => setForm((f) => ({ ...f, ...patch }))
  const ready =
    !!type &&
    form.capacity_note.trim().length >= 5 &&
    (!type.needs_organisation || form.organisation.trim().length > 0) &&
    (mode === 'classify' || (form.referee_name.trim().length > 1 && form.phone.trim().length >= 9 && form.consent))

  return (
    <div className="space-y-2 rounded-md border border-border bg-card p-3" data-testid={`referee-form-${mode}`}>
      <div className="grid gap-2 sm:grid-cols-2">
        {mode === 'add' && (
          <>
            <div>
              <Label className="text-xs">Referee's full name</Label>
              <Input value={form.referee_name} onChange={(e) => set({ referee_name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Referee's own phone</Label>
              <Input value={form.phone} inputMode="tel" onChange={(e) => set({ phone: e.target.value })} />
            </div>
          </>
        )}
        <div className="sm:col-span-2">
          <Label className="text-xs">Who the referee is to him</Label>
          <NativeSelect value={form.relationship_type} onChange={(e) => set({ relationship_type: e.target.value })}>
            <option value="">Choose one</option>
            {types.map((t) => (
              <option key={t.type_key} value={t.type_key}>
                {t.label}
              </option>
            ))}
          </NativeSelect>
          <p className="mt-1 text-[11px] text-muted-foreground">{authorityLine(types)} Family, friends and colleagues can only be the second.</p>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">In what capacity the referee knew him</Label>
          <Input
            value={form.capacity_note}
            placeholder="My shift supervisor at Shoprite Boksburg"
            onChange={(e) => set({ capacity_note: e.target.value })}
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Company, fleet or organisation{type?.needs_organisation ? '' : ' (if any)'}</Label>
          <Input value={form.organisation} onChange={(e) => set({ organisation: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Known from, roughly</Label>
          <Input type="date" value={form.known_from} onChange={(e) => set({ known_from: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Known to, roughly (leave empty if still)</Label>
          <Input type="date" value={form.known_to} onChange={(e) => set({ known_to: e.target.value })} />
        </div>
      </div>
      {mode === 'add' && (
        <label className="flex items-start gap-2 text-xs text-foreground">
          <Checkbox checked={form.consent} onCheckedChange={(v) => set({ consent: v === true })} />
          <span>He consents to Fleetly contacting this person about his application (POPIA).</span>
        </label>
      )}
      {mode === 'classify' && (
        <p className="text-[11px] text-muted-foreground">This referee was recorded before the type existed. You classify it once; it is not changed afterwards.</p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="brand" onClick={() => onSubmit(form)} disabled={!ready || busy}>
          {busy && <Spinner className="h-4 w-4" />}
          {mode === 'add' ? 'Add referee' : 'Save, once'}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// A reason, for the two controls that need one
// ---------------------------------------------------------------------------

export function ReasonControl({
  buttonLabel,
  title,
  note,
  placeholder,
  confirmLabel,
  busy,
  onSubmit,
  testId,
}: {
  buttonLabel: string
  title: string
  note: string
  placeholder: string
  confirmLabel: string
  busy: boolean
  onSubmit: (reason: string) => Promise<boolean>
  testId: string
}) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={testId}>
        <ShieldAlert className="h-4 w-4" /> {buttonLabel}
      </Button>
    )
  }
  return (
    <div className="space-y-2 rounded-md border border-border bg-card p-3" data-testid={`${testId}-form`}>
      <p className="text-xs font-medium text-foreground">{title}</p>
      <Textarea value={reason} rows={2} placeholder={placeholder} onChange={(e) => setReason(e.target.value)} />
      <p className="text-[11px] text-muted-foreground">{note}</p>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="brand"
          disabled={reason.trim().length < 10 || busy}
          onClick={async () => {
            if (await onSubmit(reason.trim())) {
              setOpen(false)
              setReason('')
            }
          }}
        >
          {busy && <Spinner className="h-4 w-4" />}
          {confirmLabel}
        </Button>
        <Button size="sm" variant="outline" onClick={() => setOpen(false)} disabled={busy}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
